import React, { useEffect, useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import PageHeader from '../components/PageHeader'
import TableFilters from '../components/TableFilters'
import AddServiceModal from '../components/AddServiceModal'

const PAY_STATUSES = ['Pending', 'Received', 'Overdue', 'Waived']
const PROJECT_TYPES = ['Website', 'ERP', 'App', 'AMC', 'Custom Software', 'Other']

function fmt(n) { return '₹' + Number(n).toLocaleString('en-IN') }

function typeBadge(t) {
  const map = { ERP: 'badge-purple', Website: 'badge-blue', App: 'badge-blue', AMC: 'badge-green', 'Custom Software': 'badge-purple' }
  return 'badge ' + (map[t] || 'badge-gray')
}

function payBadge(s) {
  const map = { Received: 'badge-green', Pending: 'badge-amber', Overdue: 'badge-red', Waived: 'badge-gray' }
  return 'badge ' + (map[s] || 'badge-gray')
}

function statusBadge(s) {
  const map = { Running: 'badge-green', 'Not Started': 'badge-gray', Paused: 'badge-amber', Stopped: 'badge-red' }
  return 'badge ' + (map[s] || 'badge-gray')
}

const FILTERS = [
  { key: 'search', label: 'Search', type: 'text', fields: ['client_name', 'brand_name', 'project_type'] },
  { key: 'status', label: 'Status', type: 'select', options: ['Not Started', 'Running', 'Paused', 'Stopped'] },
  { key: 'project_type', label: 'Type', type: 'select', options: PROJECT_TYPES },
]

function ArchiveModal({ service, onClose, onArchived }) {
  const [status, setStatus] = useState('Paused')
  const [saving, setSaving] = useState(false)

  async function doArchive() {
    setSaving(true)
    await supabase.from('services').update({ status }).eq('id', service.id)
    onArchived(service.id)
    setSaving(false)
    onClose()
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 420 }}>
        <div className="modal-header">
          <h3>Pause / Stop Project</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div style={{ background: 'var(--surface2)', borderRadius: 'var(--radius)', padding: 12, marginBottom: 16, fontSize: 13 }}>
            <strong>{service.client_name}</strong> — {service.brand_name}
            <div style={{ color: 'var(--text2)', marginTop: 4, fontSize: 12 }}>{fmt(service.total_value)} · {service.project_type}</div>
          </div>
          <div className="form-field">
            <label>Status</label>
            <select value={status} onChange={e => setStatus(e.target.value)}>
              <option>Paused</option>
              <option>Stopped</option>
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={doArchive} disabled={saving}>
            {saving ? 'Saving…' : status === 'Paused' ? 'Pause Project' : 'Stop Project'}
          </button>
        </div>
      </div>
    </div>
  )
}

function BillingHistoryModal({ service, onClose }) {
  const [records, setRecords] = useState([])
  useEffect(() => {
    supabase.from('billing_records')
      .select('*').eq('project_table', 'services').eq('project_id', service.id)
      .order('billing_date', { ascending: false })
      .then(({ data }) => setRecords(data || []))
  }, [service.id])

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 600 }}>
        <div className="modal-header">
          <h3>Billing History</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body" style={{ padding: 0 }}>
          <div style={{ padding: '16px 24px', background: 'var(--surface2)', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
            <strong>{service.client_name}</strong> — {service.brand_name}
            <div style={{ color: 'var(--text2)', marginTop: 4, fontSize: 12 }}>{fmt(service.total_value)} · {service.milestones?.length || 0} milestone(s)</div>
          </div>
          {records.length > 0 ? (
            <table>
              <thead>
                <tr><th>Date</th><th>Amount</th><th>Status</th><th>Paid on</th><th>Description</th></tr>
              </thead>
              <tbody>
                {records.map(r => (
                  <tr key={r.id}>
                    <td>{r.billing_date}</td>
                    <td style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(r.amount)}</td>
                    <td><span className={r.status === 'Paid' ? 'badge badge-green' : r.status === 'Overdue' ? 'badge badge-red' : r.status === 'Waived' ? 'badge badge-gray' : 'badge badge-amber'}>{r.status}</span></td>
                    <td style={{ color: 'var(--text2)' }}>{r.paid_date || '—'}</td>
                    <td style={{ color: 'var(--text2)', fontSize: 12 }}>{r.description || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text3)' }}>No billing records yet</div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  )
}

export default function Development() {
  const [services, setServices] = useState([])       // services rows enriched with client_name, brand_name, milestones[]
  const [clientsMap, setClientsMap] = useState({})
  const [brandsMap, setBrandsMap] = useState({})
  const [filtered, setFiltered] = useState([])

  const [addModal, setAddModal] = useState(false)
  const [editService, setEditService] = useState(null)
  const [archiveService, setArchiveService] = useState(null)
  const [billingService, setBillingService] = useState(null)

  const handleFiltered = useCallback(d => setFiltered(d), [])

  useEffect(() => {
    async function load() {
      const [sRes, mRes, cRes, bRes] = await Promise.all([
        supabase.from('services')
          .select('*')
          .eq('service_type', 'Development')
          .not('status', 'in', '("Paused","Stopped")')
          .order('created_at', { ascending: false }),
        supabase.from('milestones').select('*').order('created_at'),
        supabase.from('clients').select('id, name'),
        supabase.from('brands').select('id, name'),
      ])
      const cm = {}; (cRes.data || []).forEach(c => { cm[c.id] = c.name })
      const bm = {}; (bRes.data || []).forEach(b => { bm[b.id] = b.name })
      setClientsMap(cm)
      setBrandsMap(bm)

      const allMilestones = mRes.data || []
      const rows = (sRes.data || []).map(s => ({
        ...s,
        client_name: cm[s.client_id] || '—',
        brand_name: bm[s.brand_id] || '—',
        milestones: allMilestones.filter(m => m.service_id === s.id),
      }))
      setServices(rows)
    }
    load()
  }, [])

  function enrich(s, cm, bm, allMilestones) {
    return {
      ...s,
      client_name: (cm || clientsMap)[s.client_id] || '—',
      brand_name: (bm || brandsMap)[s.brand_id] || '—',
      milestones: allMilestones || [],
    }
  }

  function onSaved(service) {
    // Re-fetch milestones for this service and refresh maps
    Promise.all([
      supabase.from('clients').select('id, name'),
      supabase.from('brands').select('id, name'),
      supabase.from('milestones').select('*').eq('service_id', service.id).order('created_at'),
    ]).then(([cRes, bRes, mRes]) => {
      const cm = {}; (cRes.data || []).forEach(c => { cm[c.id] = c.name })
      const bm = {}; (bRes.data || []).forEach(b => { bm[b.id] = b.name })
      setClientsMap(cm); setBrandsMap(bm)
      const enriched = enrich(service, cm, bm, mRes.data || [])
      if (editService) {
        setServices(prev => prev.map(s => s.id === service.id ? enriched : s))
      } else {
        setServices(prev => [...prev, enriched])
      }
    })
    setAddModal(false)
    setEditService(null)
  }

  function onArchived(id) {
    setServices(prev => prev.filter(s => s.id !== id))
  }

  async function updateMilestonePayStatus(milestoneId, serviceId, newStatus) {
    await supabase.from('milestones').update({ payment_status: newStatus }).eq('id', milestoneId)
    if (newStatus === 'Received') {
      const svc = services.find(s => s.id === serviceId)
      const ms = svc?.milestones?.find(m => m.id === milestoneId)
      if (svc && ms) {
        const today = new Date().toISOString().slice(0, 10)
        await supabase.from('billing_records').insert([{
          client_id: svc.client_id,
          brand_id: svc.brand_id,
          project_table: 'services',
          project_id: serviceId,
          amount: Number(ms.milestone_amount) || 0,
          billing_date: today,
          due_date: ms.due_date || today,
          status: 'Paid',
          paid_date: today,
          description: svc.brand_name + ' — ' + ms.milestone_name + ' — Payment received',
        }]).select()
      }
    }
    setServices(prev => prev.map(s => s.id === serviceId
      ? { ...s, milestones: s.milestones.map(m => m.id === milestoneId ? { ...m, payment_status: newStatus } : m) }
      : s
    ))
  }

  async function remove(id) {
    if (!window.confirm('Delete this project and all its milestones?')) return
    await supabase.from('services').delete().eq('id', id)
    setServices(prev => prev.filter(s => s.id !== id))
  }

  const totalMilestoneValue = services.reduce((sum, s) =>
    sum + (s.milestones || []).reduce((ms, m) => ms + (Number(m.milestone_amount) || 0), 0), 0)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <PageHeader
        title="Active Projects — Development"
        subtitle={`${services.length} projects · ${fmt(totalMilestoneValue)} total milestones`}
        action={<button className="btn btn-primary" onClick={() => setAddModal(true)}>+ Add project</button>}
      />
      <div style={{ padding: 28, flex: 1, overflowX: 'auto' }}>
        <TableFilters filters={FILTERS} data={services} onFiltered={handleFiltered} />
        <div className="card" style={{ overflowX: 'auto' }}>
          <table style={{ minWidth: 1000 }}>
            <thead>
              <tr>
                <th>Client</th>
                <th>Brand / Project</th>
                <th>Type</th>
                <th>Total value</th>
                <th>Status</th>
                <th>Milestone</th>
                <th>Amount</th>
                <th>Due date</th>
                <th>Payment</th>
                <th>Assigned</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(s => {
                const milestones = s.milestones || []
                if (milestones.length === 0) {
                  // Service with no milestones yet
                  return (
                    <tr key={s.id}>
                      <td style={{ fontWeight: 600 }}>{s.client_name}</td>
                      <td>{s.brand_name}</td>
                      <td><span className={typeBadge(s.project_type)}>{s.project_type}</span></td>
                      <td style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>{fmt(s.total_value)}</td>
                      <td><span className={statusBadge(s.status)}>{s.status}</span></td>
                      <td colSpan={3} style={{ color: 'var(--text3)', fontSize: 12, fontStyle: 'italic' }}>No milestones yet</td>
                      <td style={{ color: 'var(--text2)' }}>{s.assigned_to || '—'}</td>
                      <td>
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => setEditService(s)}>Edit</button>
                          <button className="btn" style={{ padding: '4px 8px', fontSize: 11 }} onClick={() => setBillingService(s)}>Bills</button>
                          <button className="btn" style={{ padding: '4px 8px', fontSize: 11, color: 'var(--warning)', borderColor: 'var(--warning)' }} onClick={() => setArchiveService(s)}>Pause</button>
                          <button className="btn btn-danger" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => remove(s.id)}>Del</button>
                        </div>
                      </td>
                    </tr>
                  )
                }
                return milestones.map((m, mi) => (
                  <tr key={m.id} style={mi > 0 ? { background: 'var(--surface2)' } : {}}>
                    <td style={{ fontWeight: mi === 0 ? 600 : 400, color: mi === 0 ? 'var(--text)' : 'var(--text2)', paddingLeft: mi === 0 ? 16 : 28 }}>
                      {mi === 0 ? s.client_name : ''}
                    </td>
                    <td style={{ color: mi === 0 ? 'var(--text)' : 'transparent' }}>{mi === 0 ? s.brand_name : ''}</td>
                    <td>{mi === 0 ? <span className={typeBadge(s.project_type)}>{s.project_type}</span> : ''}</td>
                    <td style={{ fontVariantNumeric: 'tabular-nums', color: mi === 0 ? 'var(--text)' : 'transparent', fontWeight: mi === 0 ? 500 : 400 }}>
                      {mi === 0 ? fmt(s.total_value) : ''}
                    </td>
                    <td>{mi === 0 ? <span className={statusBadge(s.status)}>{s.status}</span> : ''}</td>
                    <td style={{ color: 'var(--text2)', fontSize: 13 }}>{m.milestone_name}</td>
                    <td style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>{fmt(m.milestone_amount)}</td>
                    <td style={{ color: 'var(--text2)' }}>{m.due_date || '—'}</td>
                    <td>
                      <select
                        className={payBadge(m.payment_status)}
                        value={m.payment_status}
                        onChange={e => updateMilestonePayStatus(m.id, s.id, e.target.value)}
                        style={{ border: 'none', cursor: 'pointer', fontFamily: 'var(--font)', fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 20, appearance: 'auto' }}
                      >
                        {PAY_STATUSES.map(p => <option key={p}>{p}</option>)}
                      </select>
                    </td>
                    <td style={{ color: 'var(--text2)' }}>{mi === 0 ? (s.assigned_to || '—') : ''}</td>
                    <td>
                      {mi === 0 && (
                        <div style={{ display: 'flex', gap: 4 }}>
                          <button className="btn" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => setEditService(s)}>Edit</button>
                          <button className="btn" style={{ padding: '4px 8px', fontSize: 11 }} onClick={() => setBillingService(s)}>Bills</button>
                          <button className="btn" style={{ padding: '4px 8px', fontSize: 11, color: 'var(--warning)', borderColor: 'var(--warning)' }} onClick={() => setArchiveService(s)}>Pause</button>
                          <button className="btn btn-danger" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => remove(s.id)}>Del</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={11} style={{ textAlign: 'center', padding: 40, color: 'var(--text3)' }}>No development projects yet</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {addModal && (
        <AddServiceModal
          lockedServiceType="Development"
          onClose={() => setAddModal(false)}
          onSaved={onSaved}
        />
      )}

      {editService && (
        <AddServiceModal
          lockedServiceType="Development"
          editService={editService}
          onClose={() => setEditService(null)}
          onSaved={onSaved}
        />
      )}

      {archiveService && (
        <ArchiveModal
          service={archiveService}
          onClose={() => setArchiveService(null)}
          onArchived={onArchived}
        />
      )}

      {billingService && (
        <BillingHistoryModal
          service={billingService}
          onClose={() => setBillingService(null)}
        />
      )}
    </div>
  )
}
